/**
 * AgentMultiSelectField Component
 *
 * A multi-select field for choosing team member agents from the available agents list.
 * Displays agents as selectable chips/tags with the ability to add/remove.
 */

import React, { useMemo } from 'react';
import { X, Users } from 'lucide-react';
import { useAgentsListQuery } from '@/lib/queries';
import type { FieldSchema } from '@/config/fieldConfig';

// Helper to get string value from schema field (which might be string or function)
const getStringValue = (value: string | (() => string) | Record<string, string> | undefined): string => {
  if (typeof value === 'function') return value();
  if (typeof value === 'string') return value;
  return '';
};

interface AgentMultiSelectFieldProps {
  name: string;
  value: string[] | null;
  onChange: (value: string[]) => void;
  schema: FieldSchema;
  error?: string;
  disabled?: boolean;
  currentAgentType?: string; // To exclude current agent from selection
}

export const AgentMultiSelectField: React.FC<AgentMultiSelectFieldProps> = ({
  name: _name, // Kept for interface consistency
  value,
  onChange,
  schema,
  error,
  disabled,
  currentAgentType,
}) => {
  // Fetch available agents
  const { data: agents, isLoading } = useAgentsListQuery(false);

  // Normalize value to array
  const selectedAgents = useMemo(() => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return [];
  }, [value]);

  // Filter out current agent and already selected agents for the dropdown
  const availableAgents = useMemo(() => {
    if (!agents) return [];
    return agents.filter(
      (agent) =>
        agent.agent_type !== currentAgentType &&
        !selectedAgents.includes(agent.agent_type)
    );
  }, [agents, currentAgentType, selectedAgents]);

  // Get agent info by type
  const getAgentInfo = (agentType: string) => {
    return agents?.find((a) => a.agent_type === agentType);
  };

  // Handle adding an agent
  const handleAddAgent = (agentType: string) => {
    if (!selectedAgents.includes(agentType)) {
      onChange([...selectedAgents, agentType]);
    }
  };

  // Handle removing an agent
  const handleRemoveAgent = (agentType: string) => {
    onChange(selectedAgents.filter((a) => a !== agentType));
  };

  return (
    <div className="space-y-2">
      {/* Label */}
      <div className="flex items-center gap-2">
        <Users className="w-4 h-4 text-slate-500" />
        <label className="text-sm font-medium text-slate-700">
          {getStringValue(schema.label)}
        </label>
      </div>

      {/* Selected agents as chips */}
      <div className="flex flex-wrap gap-2 min-h-[36px] p-2 bg-slate-50 rounded-lg border border-slate-200">
        {selectedAgents.length === 0 ? (
          <span className="text-sm text-slate-400 py-1">
            {isLoading ? '加载中...' : '未选择团队成员'}
          </span>
        ) : (
          selectedAgents.map((agentType) => {
            const agentInfo = getAgentInfo(agentType);
            return (
              <span
                key={agentType}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm bg-purple-100 text-purple-800 rounded-full border border-purple-200"
              >
                <span className="font-medium">{agentInfo?.name || agentType}</span>
                <span className="text-purple-500 text-xs">({agentType})</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleRemoveAgent(agentType)}
                    className="ml-1 p-0.5 hover:bg-purple-200 rounded-full transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            );
          })
        )}
      </div>

      {/* Add agent dropdown */}
      {!disabled && (
        <div className="flex items-center gap-2">
          <select
            className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
            value=""
            onChange={(e) => {
              if (e.target.value) {
                handleAddAgent(e.target.value);
              }
            }}
            disabled={isLoading || availableAgents.length === 0}
          >
            <option value="">
              {isLoading
                ? '加载Agent列表...'
                : availableAgents.length === 0
                ? '没有可添加的Agent'
                : '+ 添加团队成员'}
            </option>
            {availableAgents.map((agent) => (
              <option key={agent.agent_type} value={agent.agent_type}>
                {agent.name} ({agent.agent_type})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Help text */}
      {schema.help && (
        <p className="text-xs text-slate-500">{getStringValue(schema.help)}</p>
      )}

      {/* Error message */}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default AgentMultiSelectField;
